import React, { useState } from 'react';
import { X } from 'lucide-react';

interface AuthModalProps {
  onClose: () => void;
  onAuthSuccess: (sessionId: string, email: string) => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body: any = { email, password };
      if (!isLogin) body.name = name;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data: any = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (_) {
        data = { error: raw || 'Invalid server response' };
      }
      if (!res.ok)
        throw new Error(data.error || res.statusText || 'Auth failed');
      const sessionId = data.sessionId; // present on login
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('userEmail', data.user.email);
        onAuthSuccess(sessionId, data.user.email);
      } else {
        // registration success -> switch to login
        setIsLogin(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='fixed inset-0 z-50 grid place-items-center bg-black/80'>
      <div className='w-full max-w-sm rounded-lg border bg-background p-6 shadow-lg'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>
            {isLogin ? 'Sign In' : 'Sign Up'}
          </h2>
          <button
            className='text-muted-foreground hover:text-foreground'
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        {error && (
          <div className='mb-2 rounded-md bg-destructive text-destructive-foreground px-3 py-2 text-sm'>
            {error}
          </div>
        )}
        <form onSubmit={submit} className='space-y-3'>
          {!isLogin && (
            <div className='space-y-1'>
              <label className='text-sm text-muted-foreground'>Name</label>
              <input
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
          )}
          <div className='space-y-1'>
            <label className='text-sm text-muted-foreground'>Email</label>
            <input
              type='email'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div className='space-y-1'>
            <label className='text-sm text-muted-foreground'>Password</label>
            <input
              type='password'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          <div className='flex justify-end gap-2 pt-2'>
            <button
              type='button'
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2'
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? 'Need an account?' : 'Have an account?'}
            </button>
            <button
              type='submit'
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2'
              disabled={loading}
            >
              {loading ? '...' : isLogin ? 'Sign In' : 'Sign Up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
